import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BookOpen, Zap, Users, BarChart3, Globe, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { INTERGUIDE_LOGO_MAIN_280_URL, INTERGUIDE_NEW_LOGO_280_URL, INTERGUIDE_NEW_LOGO_640_URL, INTERGUIDE_LOGO_ALT, INTERGUIDE_NEW_WEBM_URL } from '../utils/logo';

const LandingPage = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: t('landing.featureInteractiveTitle'),
      description: t('landing.featureInteractiveDesc')
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: t('landing.featureBuilderTitle'),
      description: t('landing.featureBuilderDesc')
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: t('landing.featureMultiTenantTitle'),
      description: t('landing.featureMultiTenantDesc')
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: t('landing.featureAnalyticsTitle'),
      description: t('landing.featureAnalyticsDesc')
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: t('landing.featureEmbeddableTitle'),
      description: t('landing.featureEmbeddableDesc')
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: t('landing.featurePrivacyTitle'),
      description: t('landing.featurePrivacyDesc')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-slate-200/50" data-nav-panel>
        {/* TEMP – mobile-only nav background test */}
        <style jsx>{`
          @media (max-width: 768px) {
            [data-nav-panel] {
              background-color: #000 !important;
              background-image: none !important;
              backdrop-filter: none !important;
            }
          }
          @media (max-width: 768px) {
            [data-hero-panel] {
              background-color: #000;
            }
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <video
              src={INTERGUIDE_LOGO_MAIN_280_URL}
              alt={INTERGUIDE_LOGO_ALT}
              className="h-14 w-auto object-contain"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:text-white" data-testid="nav-login-button">{t('common.login')}</Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="nav-signup-button">{t('portal.getStarted')}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" data-hero-panel>
        {/* TEMP – mobile-only hero background test */}
        <style jsx>{`
          @media (max-width: 768px) {
            [data-hero-panel] {
              background-color: #000;
            }
          }
        `}</style>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-8">
              <video
                src={INTERGUIDE_NEW_WEBM_URL}
                alt={INTERGUIDE_LOGO_ALT}
                className="h-64 md:h-80 lg:h-96 w-auto object-contain"
                autoPlay
                muted
                loop
                playsInline
                onError={(e) => {
                  // Fallback to the original logo if video fails to load
                  const fallbackImg = document.createElement('img');
                  fallbackImg.src = INTERGUIDE_NEW_LOGO_640_URL;
                  fallbackImg.alt = INTERGUIDE_LOGO_ALT;
                  fallbackImg.className = 'h-64 md:h-80 lg:h-96 w-auto object-contain';
                  e.target.parentNode.replaceChild(fallbackImg, e.target);
                }}
              />
            </div>
            <h1 className="text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6 tracking-tight">
              {t('landing.heroTitle')}
              <span className="text-primary block mt-2">{t('landing.heroSubtitle')}</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              {t('landing.heroDescription')}
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-8" data-testid="hero-get-started-button">
                  {t('landing.getStartedFree')}
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="rounded-full px-8 text-white border-white hover:bg-white hover:text-slate-900" data-testid="hero-view-demo-button">
                {t('landing.viewDemo')}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-heading font-bold text-foreground mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t('landing.featuresSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-xl p-6 hover:shadow-soft-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-12 text-center"
          >
            <h2 className="text-4xl font-heading font-bold text-foreground mb-4">
              {t('landing.ctaTitle')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {t('landing.ctaDescription')}
            </p>
            <Link to="/signup">
              <Button size="lg" className="rounded-full px-8" data-testid="cta-get-started-button">
                {t('landing.startBuildingNow')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-muted-foreground py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-6">
            <video
              src={INTERGUIDE_NEW_LOGO_280_URL}
              alt={INTERGUIDE_LOGO_ALT}
              className="h-8 w-auto object-contain"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
          <div className="flex flex-wrap justify-center gap-6 mb-6 text-sm">
            <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
              {t('auth.termsOfService')}
            </Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
              {t('auth.privacyPolicy')}
            </Link>
            <Link to="/billing-policy" className="text-muted-foreground hover:text-foreground transition-colors">
              {t('auth.subscriptionBillingPolicy')}
            </Link>
          </div>
          <p className="text-center">{t('landing.copyright')}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;